import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, ArrowLeftRight, Printer } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { AsOfStrip } from '@/components/common/AsOfStrip';
import { Answer } from '@/components/common/Answer';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { MarkCaseControl } from '@/components/common/WorkspaceControls';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ApiError } from '@/lib/api';
import { fetchCadeCase } from '@/services/cade';
import { DecisionCard } from '@/pages/cade/DecisionCard';
import { CaseAudioPanel } from '@/pages/cade/CaseAudioPanel';
import { JudicialPanel } from '@/pages/cade/JudicialPanel';
import { RegisterTimeline } from '@/pages/cade/RegisterTimeline';
import { CodingsPanel } from '@/pages/cade/CodingsPanel';
import { SeiPanel } from '@/pages/cade/SeiPanel';
import { Stat } from '@/pages/cade/Stat';
import type {
  CadeAttributionReach,
  CadeCaseRegister,
  CadeComparable,
  CadeComparatorReach,
  CadePartyIdentity,
} from '@/services/types';

/**
 * One CADE case.
 *
 * The Tribunal is the only corpus in this product where a named individual and a
 * decision sit on the same row, so the votes panel is the differentiated content
 * here. It is also where the temptation lives: three votes on one decision are
 * worth reading and are not a basis for any rate, which is why this page shows
 * positions as recorded and derives nothing from them.
 */
export function CadeCaseDetailPage() {
  const { t } = useTranslation(['cade', 'common']);
  const { key = '' } = useParams();

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['cade', 'case', key],
    queryFn: () => fetchCadeCase(key),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={3} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }

  if (error) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="space-y-4 p-6">
        <BackLink />
        <ErrorMessage
          message={notFound ? t('cade:case.notFound') : error.message}
          onRetry={notFound ? undefined : () => void refetch()}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <BackLink />

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-lg font-semibold tabular-nums text-foreground">{data.case_number}</h1>
          <p className="text-sm text-muted-foreground">{data.case_type ?? t('cade:case.noType')}</p>
          <Link
            to={`/cade/dossier/${data.case_key}`}
            className="text-sm text-primary underline underline-offset-4"
          >
            {t('cade:dossier.link')}
          </Link>
          {/* The dates a litigator asks for first, in the only form this corpus
              can give them: the filing year from the process number, the
              archive years of the sessions, and their difference. Year
              granularity, stated as such -- never "N years and M months". */}
          <p className="text-xs text-muted-foreground" data-case-dates>
            {data.filing_year !== null
              ? t('cade:case.dates.filed', { year: data.filing_year })
              : null}
            {data.first_session_year !== null && data.last_session_year !== null ? (
              <>
                {data.filing_year !== null ? ' · ' : ''}
                {t('cade:case.dates.sessions', {
                  count: data.sessions,
                  window:
                    data.first_session_year === data.last_session_year
                      ? String(data.first_session_year)
                      : t('common:asOf.yearWindow', {
                          first: data.first_session_year,
                          last: data.last_session_year,
                        }),
                })}
              </>
            ) : null}
            {data.years_to_last_session !== null ? (
              <>
                {' · '}
                {t('cade:case.dates.duration', { count: data.years_to_last_session })}
              </>
            ) : null}
            {/* This case's own span is on the archive basis; the ladder for its
                TYPE is on the register's. Side by side by name, never mixed. */}
            {' · '}
            <Link
              to="/cade/activity#time-to-decision"
              className="text-primary underline underline-offset-4"
            >
              {t('cade:case.dates.byType')}
            </Link>
          </p>
        </div>
        {/* One row per DECISION, matching what this page lists. Every sibling
            detail page in the product exported and neither CADE one did. */}
        <div className="flex flex-wrap items-start gap-2">
          <MarkCaseControl agency="cade" caseNumber={data.case_number} />
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm text-foreground"
          >
            <Printer className="h-3.5 w-3.5" aria-hidden />
            {t('common:export.print')}
          </button>
          <Link
            to={`/cade/compare?a=${encodeURIComponent(data.case_key)}`}
            className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm text-foreground"
            data-compare-link
          >
            <ArrowLeftRight className="h-3.5 w-3.5" aria-hidden />
            {t('cade:compare.link')}
          </Link>
          <ExportButton path={`/api/v1/cade/export/c/${encodeURIComponent(key)}`} />
        </div>
      </header>

      <ExplainerCard i18nKey="cade:case.explainer" />

      {/* Repeated per screen, not only on the list: a reader can land here from a
          search result without ever seeing the party-identity warning.

          Driven by the MEASURED answer, which the backend computes on every
          request and this page ignored until 2026-09-10 -- it rendered
          `case.partiesUnresolved` unconditionally instead, so the page would have
          gone on asserting "no record was merged" after the merge happened. It is
          already moving: `parties.canonical_cluster_id` groups 88 clusters over
          195 rows while `name_canonical` still merges none. `/cade/cases` has
          always branched on `identity.resolved`; this is the same branch, in the
          case page's own shorter wording. */}
      <Answer answer={data.party_identity} scope="corpus">
        {(identity: CadePartyIdentity) => (
          <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-foreground">
            <AlertTriangle
              className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400"
              aria-hidden
            />
            <p>
              {identity.resolved
                ? t('cade:cases.parties.resolved', { count: identity.merged })
                : t('cade:case.partiesUnresolved')}
            </p>
          </div>
        )}
      </Answer>

      <AsOfStrip asOf={data.as_of} />

      {/* The matter's dated milestones, from the published register: a different
          population from the session archive below, on a different basis (the day,
          not the folder year). `record` scope: a matter the register does not list
          is a fact about THIS matter -- not public, or decided before 2015 -- and
          the corpus badge would say CADE has no register at all. */}
      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-1 text-sm font-semibold text-foreground">
          {t('cade:case.register.title')}
        </h2>
        <p className="mb-3 text-xs text-muted-foreground">{t('cade:case.register.subtitle')}</p>
        <Answer answer={data.register} scope="record" help={t('cade:case.register.help')}>
          {(register: CadeCaseRegister) => <RegisterTimeline register={register} />}
        </Answer>
        <CaseAudioPanel caseKey={data.case_key} />
        <JudicialPanel caseKey={data.case_key} />
      </section>

      {/* A third source: SEI's own list of the process's documents, dated by SEI, in its own
          lane. Never merged with the register's milestones above or the archive below. */}
      <SeiPanel caseKey={data.case_key} />

      {/* What the votes say, read by a model and served only where two models agree and the
          quote verifies. After the SEI documents it is read from, before the archive's own
          figures, and never merged with either. */}
      <CodingsPanel caseKey={data.case_key} codings={data.codings} />

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('cade:case.attribution.title')}
        </h2>
        {/* `corpus`, and stated rather than defaulted. The refusal beneath this
            is genuinely corpus-wide -- "only N fine attributions exist across the
            corpus" -- so "Indisponivel para esta agencia" is the accurate badge.
            It read `corpus` before only because that is the default and no call
            site had been asked the question. */}
        <Answer
          answer={data.attribution_reach}
          scope="corpus"
          help={t('cade:case.attribution.help')}
        >
          {(reach) => <AttributionPanel reach={reach} />}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="cade:case.guide.attribution" />
        </div>
      </section>

      {/* Comparator candidates, above the decisions: a reader looking for "what
          happened in similar cases" should not have to scroll past this case's
          own decisions to find them. */}
      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-1 text-sm font-semibold text-foreground">
          {t('cade:case.comparables.title')}
        </h2>
        <p className="mb-3 text-xs text-muted-foreground">{t('cade:case.comparables.subtitle')}</p>
        {/* `record`: a case with no comparator pair is a fact about THIS case, and
            the corpus badge would tell a reader CADE supports no comparison at
            all -- wrong on 1,207 of 1,569 for the ANEEL sibling, which is where
            this defect was found and fixed with a prop that this call site then
            never passed. Latent today, because `analytics/cade/cases.py` returns
            `answered()` at zero found rather than refusing; the prop is what
            makes it stay right if that ever changes. */}
        <Answer answer={data.comparables} scope="record" help={t('cade:case.comparables.help')}>
          {(reach) => <ComparablesList reach={reach} items={data.items} />}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="cade:case.guide.comparables" />
        </div>
      </section>

      {/* Above the decisions, not below: it tells a reader how to read the cards
          they are about to scroll through. */}
      <FieldGuide i18nKey="cade:case.guide.decision" />

      {/* Bounded, with its own scrollbar, as `/cade/cases` does for its table.
          This is the longest list in CADE by a wide margin: each card expands
          into that decision's votes, its attributed fines with their evidence
          spans, its parties, its citations and the full minuted ata text, and
          the repository caps nothing. Unbounded, a case with several decisions
          puts the attribution and comparables panels above it several screens
          out of reach. */}
      {/* The region is a wrapper: `role="region"` on the list itself replaced its list
          role and left every item an orphaned listitem. */}
      <div
        className="max-h-[70vh] overflow-auto rounded-lg border p-2"
        role="region"
        aria-label={t('cade:case.listLabel')}
      >
        <ol className="space-y-3">
          {data.decisions.map((decision, index) => (
            <li key={`${decision.item_number ?? 'item'}-${decision.session_number ?? index}`}>
              <DecisionCard decision={decision} caseKey={data.case_key} />
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

/**
 * Comparable decisions, ordered so the ones worth looking at come first.
 *
 * Every badge on a row exists to stop a specific misreading. "Different outcome"
 * is the reason to look and is NOT a finding — nothing here has been adjudicated.
 * "Other case type" matters because the pairing shares citations without
 * constraining type, so it will set a merger review against a cartel
 * prosecution. "Procedural" marks a side that is a pedido de vista, an
 * adjournment rather than a ruling, which is not a divergence at all.
 */
function ComparablesList({
  reach,
  items,
}: {
  reach: CadeComparatorReach;
  items: CadeComparable[];
}) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });

  // The common case: seven cases in ten share a legal basis with nothing. Said
  // here rather than as a bare empty list, which would read as "this case is
  // unlike every other one" -- a claim, and a false one.
  if (items.length === 0) {
    return (
      <p className="rounded border border-dashed bg-muted/30 p-3 text-xs text-muted-foreground">
        {t('cade:case.comparables.none', {
          withComparables: number.format(reach.cases_with_comparables),
          cases: number.format(reach.cases),
          share: new Intl.NumberFormat(i18n.language, {
            style: 'percent',
            maximumFractionDigits: 1,
          }).format(reach.corpus_share),
        })}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {reach.truncated > 0 ? (
        <p className="text-xs text-muted-foreground">
          {t('cade:case.comparables.truncated', {
            shown: number.format(reach.shown),
            found: number.format(reach.found),
          })}
        </p>
      ) : null}

      <ul className="space-y-2">
        {items.map((item, index) => (
          <li
            key={`${item.case_key}-${item.item_number ?? index}`}
            className="rounded border p-3 text-xs"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <Link
                to={`/cade/c/${item.case_key}`}
                className="font-medium tabular-nums text-primary underline-offset-4 hover:underline"
              >
                {item.case_number}
              </Link>
              <span className="tabular-nums text-muted-foreground">
                {t('cade:case.comparables.shared', { count: item.shared_count })}
              </span>
            </div>

            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {item.same_outcome === false ? (
                <span className="rounded border border-amber-500/50 bg-amber-500/10 px-2 py-0.5 text-amber-700 dark:text-amber-400">
                  {t('cade:case.comparables.differentOutcome')}
                </span>
              ) : null}
              {!item.same_case_type ? (
                <span className="rounded border px-2 py-0.5 text-muted-foreground">
                  {t('cade:case.comparables.otherCaseType')}
                </span>
              ) : null}
              {item.procedural ? (
                <span className="rounded border px-2 py-0.5 text-muted-foreground">
                  {t('cade:case.comparables.procedural')}
                </span>
              ) : null}
              {(item.outcome_codes.length === 0 ? ['UNCLASSIFIED'] : item.outcome_codes).map(
                (code) => (
                  <span key={code} className="rounded bg-muted px-2 py-0.5 text-muted-foreground">
                    {t(`cade:overview.outcomes.${code}`, { defaultValue: code })}
                  </span>
                ),
              )}
              {item.fine_total_brl !== null && item.fine_total_brl > 0 ? (
                <span className="rounded border border-primary/40 bg-primary/10 px-2 py-0.5 tabular-nums text-foreground">
                  {currency.format(item.fine_total_brl)}
                </span>
              ) : null}
            </div>

            <dl className="mt-1.5 grid gap-x-4 gap-y-0.5 text-muted-foreground sm:grid-cols-3">
              <div>
                <dt className="inline">{t('cade:case.comparables.type')}: </dt>
                <dd className="inline text-foreground">{item.case_type ?? '—'}</dd>
              </div>
              <div>
                <dt className="inline">{t('cade:case.relator')}: </dt>
                <dd className="inline text-foreground">
                  {item.relator && item.relator_slug ? (
                    <Link
                      to={`/cade/r/${item.relator_slug}`}
                      className="text-primary underline-offset-4 hover:underline"
                    >
                      {item.relator}
                    </Link>
                  ) : (
                    (item.relator ?? '—')
                  )}
                </dd>
              </div>
              <div>
                <dt className="inline">{t('cade:case.comparables.session')}: </dt>
                <dd className="inline text-foreground">{item.session_date_raw ?? '—'}</dd>
              </div>
            </dl>

            {item.shared_citations.length > 0 ? (
              <ul className="mt-1.5 flex flex-wrap gap-1">
                {item.shared_citations.map((citation) => (
                  <li
                    key={citation}
                    className="rounded bg-muted px-1.5 py-0.5 text-muted-foreground"
                  >
                    {citation}
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function BackLink() {
  const { t } = useTranslation(['cade']);
  return (
    <Link
      to="/cade/cases"
      className="inline-flex items-center gap-1.5 text-sm text-primary underline-offset-4 hover:underline"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden />
      {t('cade:case.backToList')}
    </Link>
  );
}

function AttributionPanel({ reach }: { reach: CadeAttributionReach }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <div className="space-y-2 text-xs">
      <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat
          emphasis={false}
          label={t('cade:case.attribution.total')}
          value={number.format(reach.attributions)}
        />
        <Stat
          emphasis={false}
          label={t('cade:case.attribution.verified')}
          value={number.format(reach.verified)}
        />
        <Stat
          label={t('cade:case.attribution.unverified')}
          value={number.format(reach.unverified)}
        />
        <Stat
          label={t('cade:case.attribution.decisions')}
          value={`${number.format(reach.decisions_with_attribution)} / ${number.format(
            reach.decisions_with_fines,
          )}`}
        />
      </dl>
      {/* No per-extractor list here, by request (2026-09-14): a reader of a
          case does not need the model names. `reach.methods` stays on the wire
          and in the export, and each attributed fine below still names the
          extractor and prompt that produced it -- the rule that a verdict
          carries its provenance is met at the row, where it is read. The
          pattern caveat stays because it explains the badge on those rows. */}
      {reach.by_pattern > 0 ? (
        <p className="text-muted-foreground">
          {t('cade:case.attribution.patternCaveat', {
            pattern: number.format(reach.by_pattern),
            total: number.format(reach.attributions),
          })}
        </p>
      ) : null}
    </div>
  );
}
